import { useEffect, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  Download,
  Building2,
  BedDouble,
  Search,
  Eye,
  Printer,
  RefreshCw,
  CheckCircle,
  FileSpreadsheet,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import Pagination from "../../ui/pagination";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import {
  AllPropertyApi,
  getAllProperties,
  getVenues,
  VenueApi,
} from "../../services/propertyDetailsService";
import {
  DailyCheckInReportTourApi,
  DailyCheckInExportRequest,
  DailyCheckInTourFilterOptionApi,
  DailyCheckInSummaryStatsApi,
  exportDailyCheckInExcel,
  exportDailyCheckInPdf,
  getDailyCheckInReport,
  getDailyCheckInTourFilterOptions,
  getDailyCheckInSummaryStats,
} from "../../services/accommodationService";
import { FullScreenLoading } from "components/ui/loader";
import { toast } from "sonner";


import {
  AccommodationStay,
  addApplicantRoomsToStays,
  getApplicantAge,
  mapReportStays,
  sortAccommodationStays,
} from "./dailyCheckInReportUtils";

interface AccommodationReportsPageProps {
  onNavigate?: (page: string) => void;
  initialReport?: string;
}

const getLocalDateInputValue = (date: Date) => {
  const localDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60_000,
  );
  return localDate.toISOString().slice(0, 10);
};

const formatCardDate = (value: string) => {
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}-${month}-${year}` : value;
};

const formatReportDate = (value: string) =>
  value ? formatCardDate(value.slice(0, 10)) : "—";

const formatReportTimes = (value: string) =>
  value
    ? value
        .split(",")
        .map((time) => time.trim().split(":").slice(0, 2).join(":"))
        .join(", ")
    : "—";

const REPORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatLongReportDate = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split("-");
  const monthName = REPORT_MONTHS[Number(month) - 1];
  return year && monthName && day
    ? `${day} ${monthName} ${year}`
    : value || "—";
};

function StayDetailsBadges({ stays }: { stays: AccommodationStay[] }) {
  if (!stays.length) return <span className="text-slate-500">—</span>;

  return (
    <div className="w-[520px] min-w-[460px] max-w-full space-y-2">
      {stays.map((stay, index) => {
        const fromDate = stay.fromDate.replace(
          /\s+\d{2}:\d{2}(?::\d{2})?$/,
          "",
        );
        const toDate = stay.toDate.replace(/\s+\d{2}:\d{2}(?::\d{2})?$/, "");
        const fromTime = stay.fromDate.match(
          /\s+(\d{2}:\d{2})(?::\d{2})?$/,
        )?.[1];
        const toTime = stay.toDate.match(/\s+(\d{2}:\d{2})(?::\d{2})?$/)?.[1];
        const color =
          stay.venue === "Karbala"
            ? "text-amber-500"
            : stay.venue === "Najaf"
              ? "text-emerald-600"
              : "text-violet-600";

        return (
          <div
            key={`${stay.venue}:${stay.fromDate}:${index}`}
            className="flex items-center gap-2 rounded-xl border border-sky-100 bg-sky-50/60 px-3 py-2"
          >
            <svg
              viewBox="0 0 40 40"
              fill="currentColor"
              aria-hidden="true"
              className={`h-5 w-5 shrink-0 ${color}`}
            >
              <path d="M2 35h36v3H2zM5 22h4v13H5zm1-5h2v4H6zm25 5h4v13h-4zm1-5h2v4h-2zM12 25c0-6 8-7 8-13 0 6 8 7 8 13v10h-6v-7a2 2 0 0 0-4 0v7h-6zM28 9h3v26h-3zm-1-3h5v3h-5zm2-4h1v4h-1z" />
            </svg>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-slate-800">
                {stay.venue}
              </div>
              <div className="mt-0.5 text-[11px] font-semibold text-slate-700">
                {stay.property || "—"}
              </div>
              {stay.rooms && (
                <div className="mt-1 space-y-0.5 text-[10px] text-slate-600">
                  {stay.rooms.length ? stay.rooms.map((room, roomIndex) => (
                    <div key={roomIndex}>
                      <span className="font-semibold text-green-700">Room {room.roomNumber ?? "—"}</span>
                      {room.roomTypeName && <span className="font-semibold text-green-700"> · {room.roomTypeName}</span>}
                    </div>
                  )) : <span className="text-slate-500">Room assignment not provided</span>}
                </div>
              )}
            </div>
            <div className="ml-auto flex shrink-0 items-center justify-end gap-2 whitespace-nowrap text-[11px] text-slate-600">
              <CalendarDays
                className="h-3.5 w-3.5 text-sky-700"
                aria-hidden="true"
              />
              <div className="grid grid-cols-[auto_auto_auto] items-center gap-x-1 text-center">
                <span>{fromDate}</span>
                <span>-</span>
                <span>{toDate}</span>
                {(fromTime || toTime) && (
                  <>
                    <span className="mt-1 font-medium text-emerald-700">
                      {fromTime || "—"}
                    </span>
                    <span />
                    <span className="mt-1 font-medium text-rose-600">
                      {toTime || "—"}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ArrivingTourDetailsHeaderCard({
  report,
  selectedVenue,
}: {
  report: DailyCheckInReportTourApi;
  selectedVenue: string;
}) {
  const tour = report.fullDetails?.tour;
  const header = {
    tourName: report.tourName,
    tourOperatorName: report.tourOperatorName,
    totalPax: report.noOfApplicants,
    arrivalDate: tour?.arrivalDate ?? report.arrivalDate,
    departureDate: tour?.departureDate ?? "",
    arrivalFlightNo: tour?.arrivalFlightNo ?? report.arrivalFlightNo,
    arrivalPortName: tour?.arrivalPortName ?? report.arrivalPortName,
    departureFlightNo: tour?.departureFlightNo,
    departurePortName: tour?.departurePortName,
    specialAccommodationRequest: tour?.specialAccommodationRequest,
  };
  const stays = mapReportStays(report.stayDetails).filter(
    (stay) => selectedVenue === "All" || stay.venue === selectedVenue,
  );
  const travelMeans = report.travelMeans || "—";

  return (
    <div className="rounded-lg border border-sky-100 bg-sky-100 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {header.tourName}
          </h3>
          <p className="text-[10px] text-slate-500">
            {header.tourOperatorName || "—"} · {header.totalPax} applicants
          </p>
        </div>
        <Badge
          variant="outline"
          className="border-sky-200 bg-white text-sky-700"
        >
          {travelMeans}
        </Badge>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-md bg-white px-3 py-2">
          <div className="text-[9px] text-slate-400">Arrival / Departure</div>
          <div className="text-[11px] font-semibold text-emerald-700">
            Arrival: {formatLongReportDate(header.arrivalDate)} · {formatReportTimes(tour?.arrivalTime ?? report.arrivalTime ?? "")}
          </div>
          <div className="text-[11px] font-semibold text-rose-600">
            Departure: {formatLongReportDate(header.departureDate)} · {formatReportTimes(tour?.departureTime ?? "")}
          </div>
        </div>

        <div className="rounded-md bg-white px-3 py-2">
          <div className="text-[9px] text-slate-400">
            Flight / Travel Details
          </div>
          <div className="text-[11px] font-semibold text-slate-700">
            Arrival: {header.arrivalFlightNo || "—"} ·{" "}
            {header.arrivalPortName || "—"}
          </div>
          <div className="text-[11px] font-semibold text-slate-700">
            Departure: {header.departureFlightNo || "—"} ·{" "}
            {header.departurePortName || "—"}
          </div>
        </div>

        {stays.map((stay) => (
          <div
            key={`${stay.venue}:${stay.fromDate}:${stay.toDate}`}
            className="rounded-md bg-white px-3 py-2"
          >
            <div className="text-[9px] text-slate-400">{stay.venue} Stay</div>
            <div className="text-[11px] font-semibold text-slate-700">
              {stay.property || "—"}
            </div>
            <div className="whitespace-nowrap text-[9px] text-slate-500">
              In {stay.fromDate} &nbsp; Out {stay.toDate}
            </div>
          </div>
        ))}

        {header.specialAccommodationRequest?.trim() && (
          <div className="rounded-md bg-white px-3 py-2">
            <div className="text-[9px] text-slate-400">
              Special Accommodation Request
            </div>
            <div className="text-[11px] font-semibold text-slate-700">
              {header.specialAccommodationRequest}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const TODAY = getLocalDateInputValue(new Date());

const COLORS = [
  "#5B9BD5",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
];

const occupancyData = [
  { month: "Jan", karbala: 85, najaf: 72 },
  { month: "Feb", karbala: 78, najaf: 80 },
  { month: "Mar", karbala: 92, najaf: 85 },
  { month: "Apr", karbala: 88, najaf: 76 },
  { month: "May", karbala: 95, najaf: 92 },
  { month: "Jun", karbala: 82, najaf: 88 },
];

const roomTypeData = [
  { name: "Standard 2 People", value: 42 },
  { name: "Standard 4 People", value: 35 },
  { name: "Standard 8 People", value: 23 },
];

const checkInOutDetailRows = [
  {
    isoDate: "2026-08-07",
    date: "07 Aug 2026",
    venue: "Najaf",
    property: "Faiz Hussainia",
    room: "#101",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    pax: 4,
    plannedCheckIn: "02:00 PM",
    actualCheckIn: "12:30 PM",
    earlyCheckIn: "1h 30m early",
    plannedCheckOut: "11:00 AM",
    actualCheckOut: "11:00 AM",
    lateCheckOut: "On time",
    movementStatus: "Checked in",
    action: "Early check-in approved",
  },
  {
    isoDate: "2026-08-07",
    date: "07 Aug 2026",
    venue: "Najaf",
    property: "Faiz Hussainia",
    room: "#104",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    pax: 3,
    plannedCheckIn: "02:00 PM",
    actualCheckIn: "02:10 PM",
    earlyCheckIn: "Standard",
    plannedCheckOut: "11:00 AM",
    actualCheckOut: "12:45 PM",
    lateCheckOut: "1h 45m late",
    movementStatus: "Checked out",
    action: "Late check-out charge applied",
  },
  {
    isoDate: "2026-08-08",
    date: "08 Aug 2026",
    venue: "Karbala",
    property: "Al-Amin Hotel",
    room: "#201",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    pax: 2,
    plannedCheckIn: "03:00 PM",
    actualCheckIn: "01:00 PM",
    earlyCheckIn: "2h early",
    plannedCheckOut: "12:00 PM",
    actualCheckOut: "12:00 PM",
    lateCheckOut: "On time",
    movementStatus: "Checked in",
    action: "Early access included",
  },
  {
    isoDate: "2026-08-08",
    date: "08 Aug 2026",
    venue: "Karbala",
    property: "Al Kawthar Hotel",
    room: "#305",
    tour: "External Hotel Test Tour",
    operator: "External Hotel Operator",
    pax: 3,
    plannedCheckIn: "03:00 PM",
    actualCheckIn: "03:20 PM",
    earlyCheckIn: "Standard",
    plannedCheckOut: "11:00 AM",
    actualCheckOut: "02:00 PM",
    lateCheckOut: "3h late",
    movementStatus: "Checked out",
    action: "Awaiting late charge approval",
  },
  {
    isoDate: "2026-08-09",
    date: "09 Aug 2026",
    venue: "Najaf",
    property: "Grand Hotel Najaf",
    room: "#407",
    tour: "Instant Najaf Group",
    operator: "Najaf Instant Tours",
    pax: 1,
    plannedCheckIn: "02:00 PM",
    actualCheckIn: "—",
    earlyCheckIn: "—",
    plannedCheckOut: "11:00 AM",
    actualCheckOut: "—",
    lateCheckOut: "—",
    movementStatus: "Expected",
    action: "Room ready",
  },
  {
    isoDate: "2026-08-10",
    date: "10 Aug 2026",
    venue: "Karbala",
    property: "Faiz Hussainia",
    room: "#112",
    tour: "Ashara Karbala Arrival",
    operator: "Dubai Ziyarat Group",
    pax: 4,
    plannedCheckIn: "01:00 PM",
    actualCheckIn: "11:45 AM",
    earlyCheckIn: "1h 15m early",
    plannedCheckOut: "10:00 AM",
    actualCheckOut: "—",
    lateCheckOut: "—",
    movementStatus: "In house",
    action: "No action required",
  },
];

const roomAllocationReportRows = [
  {
    isoDate: "2026-08-07",
    requestId: "RR-26041",
    requestedOn: "05 Aug 2026",
    operator: "Al Burhani Tours",
    tour: "Ziyarat Najaf 2026",
    venue: "Najaf",
    stayFrom: "07 Aug 2026",
    stayTo: "10 Aug 2026",
    requestedProperty: "Faiz Hussainia",
    requestedType: "Shared Gents",
    requestedRooms: 2,
    requestedApplicants: 7,
    allocatedProperty: "Faiz Hussainia",
    allocatedRooms: "#101, #102",
    allocatedRoomCount: 2,
    allocatedType: "Shared Gents",
    allocatedApplicants: 7,
    allocatedBy: "Accommodation Manager",
    allocatedOn: "06 Aug 2026 · 04:15 PM",
    status: "Completed",
    variance: "As requested",
  },
  {
    isoDate: "2026-08-07",
    requestId: "RR-26042",
    requestedOn: "05 Aug 2026",
    operator: "Al Burhani Tours",
    tour: "Ziyarat Najaf 2026",
    venue: "Najaf",
    stayFrom: "07 Aug 2026",
    stayTo: "10 Aug 2026",
    requestedProperty: "Faiz Hussainia",
    requestedType: "Shared Ladies",
    requestedRooms: 2,
    requestedApplicants: 6,
    allocatedProperty: "Najaf Faiz",
    allocatedRooms: "#104, #105",
    allocatedRoomCount: 2,
    allocatedType: "Shared Ladies",
    allocatedApplicants: 6,
    allocatedBy: "Accommodation Manager",
    allocatedOn: "06 Aug 2026 · 04:22 PM",
    status: "Reallocated",
    variance: "Property changed due to availability",
  },
  {
    isoDate: "2026-08-08",
    requestId: "RR-26043",
    requestedOn: "06 Aug 2026",
    operator: "Saifee Tours & Travel",
    tour: "Miqaat Karbala 2026",
    venue: "Karbala",
    stayFrom: "08 Aug 2026",
    stayTo: "17 Aug 2026",
    requestedProperty: "Al-Amin Hotel",
    requestedType: "Private",
    requestedRooms: 3,
    requestedApplicants: 5,
    allocatedProperty: "Al-Amin Hotel",
    allocatedRooms: "#201, #202",
    allocatedRoomCount: 2,
    allocatedType: "Private",
    allocatedApplicants: 4,
    allocatedBy: "Accommodation Manager",
    allocatedOn: "07 Aug 2026 · 11:05 AM",
    status: "Partial",
    variance: "1 applicant and 1 room pending",
  },
  {
    isoDate: "2026-08-08",
    requestId: "RR-26044",
    requestedOn: "06 Aug 2026",
    operator: "Najaf Instant Tours",
    tour: "Instant Najaf Group",
    venue: "Najaf",
    stayFrom: "08 Aug 2026",
    stayTo: "11 Aug 2026",
    requestedProperty: "Grand Hotel Najaf",
    requestedType: "Suite",
    requestedRooms: 1,
    requestedApplicants: 2,
    allocatedProperty: "Grand Hotel Najaf",
    allocatedRooms: "#406",
    allocatedRoomCount: 1,
    allocatedType: "Suite",
    allocatedApplicants: 2,
    allocatedBy: "Accommodation Manager",
    allocatedOn: "07 Aug 2026 · 01:40 PM",
    status: "Completed",
    variance: "As requested",
  },
  {
    isoDate: "2026-08-09",
    requestId: "RR-26045",
    requestedOn: "07 Aug 2026",
    operator: "External Hotel Operator",
    tour: "External Hotel Test Tour",
    venue: "Karbala",
    stayFrom: "09 Aug 2026",
    stayTo: "15 Aug 2026",
    requestedProperty: "Al Kawthar Hotel",
    requestedType: "Suite",
    requestedRooms: 2,
    requestedApplicants: 5,
    allocatedProperty: "Al Kawthar Hotel",
    allocatedRooms: "Pending",
    allocatedRoomCount: 0,
    allocatedType: "—",
    allocatedApplicants: 0,
    allocatedBy: "—",
    allocatedOn: "—",
    status: "Pending",
    variance: "Awaiting available suite rooms",
  },
  {
    isoDate: "2026-08-10",
    requestId: "RR-26046",
    requestedOn: "08 Aug 2026",
    operator: "Burhani Family Tours",
    tour: "Family Shared Rooms Tour",
    venue: "Najaf",
    stayFrom: "10 Aug 2026",
    stayTo: "16 Aug 2026",
    requestedProperty: "Faiz Hussainia",
    requestedType: "Shared Ladies",
    requestedRooms: 2,
    requestedApplicants: 6,
    allocatedProperty: "Grand Hotel Najaf",
    allocatedRooms: "#402, #404",
    allocatedRoomCount: 2,
    allocatedType: "Shared Ladies",
    allocatedApplicants: 6,
    allocatedBy: "Super Admin",
    allocatedOn: "09 Aug 2026 · 05:10 PM",
    status: "Reallocated",
    variance: "Moved from Faiz to hotel with recorded reason",
  },
];

// const dailyAvailabilityRows = [
//   {
//     date: "18 Sep 2026",
//     venue: "Karbala",
//     property: "Faiz Hussainia",
//     type: "Faiz",
//     total: 80,
//     available: 12,
//     occupied: 62,
//     blocked: 2,
//     maintenance: 4,
//     pax: 156,
//     checkIns: 18,
//     checkOuts: 4,
//   },
//   {
//     date: "18 Sep 2026",
//     venue: "Karbala",
//     property: "Al Kawthar Hotel",
//     type: "Hotel",
//     total: 120,
//     available: 18,
//     occupied: 96,
//     blocked: 2,
//     maintenance: 4,
//     pax: 224,
//     checkIns: 27,
//     checkOuts: 8,
//   },
//   {
//     date: "18 Sep 2026",
//     venue: "Najaf",
//     property: "Najaf Faiz",
//     type: "Faiz",
//     total: 64,
//     available: 8,
//     occupied: 52,
//     blocked: 1,
//     maintenance: 3,
//     pax: 132,
//     checkIns: 14,
//     checkOuts: 6,
//   },
//   {
//     date: "19 Sep 2026",
//     venue: "Najaf",
//     property: "Grand Hotel Najaf",
//     type: "Hotel",
//     total: 95,
//     available: 21,
//     occupied: 70,
//     blocked: 1,
//     maintenance: 3,
//     pax: 168,
//     checkIns: 9,
//     checkOuts: 21,
//   },
//   {
//     date: "19 Sep 2026",
//     venue: "Karbala",
//     property: "Al-Amin Hotel",
//     type: "Hotel",
//     total: 70,
//     available: 5,
//     occupied: 61,
//     blocked: 2,
//     maintenance: 2,
//     pax: 146,
//     checkIns: 7,
//     checkOuts: 18,
//   },
// ];

const dailyTourAvailabilityRows = [
  {
    date: "07 Aug 2026",
    isoDate: "2026-08-07",
    operator: "Al Burhani Tours",
    tour: "Ziyarat Najaf 2026",
    tourType: "Faiz",
    venue: "Najaf",
    property: "Faiz Hussainia",
    travelFrom: "07 Aug 2026",
    travelTo: "14 Aug 2026",
    flightNo: "IA-281",
    airport: "Najaf International",
    arrivalTime: "10:20 AM",
    checkInTime: "02:00 PM",
    checkOutTime: "11:00 AM",
    chargeBasis: "Per Pax",
    passengers: 42,
    passengerDetails:
      "Ali Zaidi family (6), Merchant family (5), 31 other applicants",
    roomsRequested: 12,
    roomTypes: "6 Standard 4 People · 6 Standard 2 People",
    matchingRooms: 18,
    status: "Available",
  },
  {
    date: "07 Aug 2026",
    isoDate: "2026-08-07",
    operator: "Saifee Tours & Travel",
    tour: "Miqaat Karbala 2026",
    tourType: "Miqaat",
    venue: "Karbala",
    property: "Al-Amin Hotel",
    travelFrom: "07 Aug 2026",
    travelTo: "17 Aug 2026",
    flightNo: "IQ-2021",
    airport: "Baghdad International",
    arrivalTime: "11:45 AM",
    checkInTime: "03:00 PM",
    checkOutTime: "12:00 PM",
    chargeBasis: "Per Room",
    passengers: 28,
    passengerDetails:
      "Yousuf family (5), Hussein family (4), 19 other applicants",
    roomsRequested: 9,
    roomTypes: "7 Standard 4 People · 2 Standard 2 People",
    matchingRooms: 11,
    status: "Available",
  },
  {
    date: "08 Aug 2026",
    isoDate: "2026-08-08",
    operator: "Najaf Instant Tours",
    tour: "Instant Najaf Group",
    tourType: "Instant",
    venue: "Najaf",
    property: "Grand Hotel Najaf",
    travelFrom: "08 Aug 2026",
    travelTo: "11 Aug 2026",
    flightNo: "FZ-221",
    airport: "Najaf International",
    arrivalTime: "08:30 AM",
    checkInTime: "02:00 PM",
    checkOutTime: "11:00 AM",
    chargeBasis: "Per Room",
    passengers: 16,
    passengerDetails: "4 families · 16 applicants",
    roomsRequested: 6,
    roomTypes: "4 Standard 4 People · 2 Standard 2 People",
    matchingRooms: 7,
    status: "Available",
  },
  {
    date: "08 Aug 2026",
    isoDate: "2026-08-08",
    operator: "External Hotel Operator",
    tour: "External Hotel Test Tour",
    tourType: "External",
    venue: "Karbala",
    property: "Al Kawthar Hotel",
    travelFrom: "08 Aug 2026",
    travelTo: "15 Aug 2026",
    flightNo: "EK-943",
    airport: "Baghdad International",
    arrivalTime: "01:15 PM",
    checkInTime: "03:00 PM",
    checkOutTime: "11:00 AM",
    chargeBasis: "Per Room",
    passengers: 22,
    passengerDetails: "3 families · 8 individual applicants",
    roomsRequested: 8,
    roomTypes: "5 Standard 4 People · 3 Standard 2 People",
    matchingRooms: 6,
    status: "Partial",
  },
  {
    date: "09 Aug 2026",
    isoDate: "2026-08-09",
    operator: "Burhani Family Tours",
    tour: "Family Shared Rooms Tour",
    tourType: "Faiz",
    venue: "Najaf",
    property: "Najaf Faiz",
    travelFrom: "09 Aug 2026",
    travelTo: "16 Aug 2026",
    flightNo: "GF-205",
    airport: "Najaf International",
    arrivalTime: "09:10 AM",
    checkInTime: "02:00 PM",
    checkOutTime: "11:00 AM",
    chargeBasis: "Per Pax",
    passengers: 31,
    passengerDetails: "5 clubbed families · 6 individual applicants",
    roomsRequested: 10,
    roomTypes: "6 Standard 4 People · 4 Standard 2 People",
    matchingRooms: 13,
    status: "Available",
  },
  {
    date: "10 Aug 2026",
    isoDate: "2026-08-10",
    operator: "Dubai Ziyarat Group",
    tour: "Ashara Karbala Arrival",
    tourType: "Miqaat",
    venue: "Karbala",
    property: "Faiz Hussainia",
    travelFrom: "10 Aug 2026",
    travelTo: "20 Aug 2026",
    flightNo: "IA-224",
    airport: "Baghdad International",
    arrivalTime: "06:40 AM",
    checkInTime: "01:00 PM",
    checkOutTime: "10:00 AM",
    chargeBasis: "Per Pax",
    passengers: 38,
    passengerDetails: "7 families · 9 individual applicants",
    roomsRequested: 13,
    roomTypes:
      "2 Standard 8 People · 7 Standard 4 People · 4 Standard 2 People",
    matchingRooms: 9,
    status: "Attention",
  },
  {
    date: "11 Aug 2026",
    isoDate: "2026-08-11",
    operator: "London Mumineen Group",
    tour: "London Walk-in Group",
    tourType: "Walk-in",
    venue: "Najaf",
    property: "Grand Hotel Najaf",
    travelFrom: "11 Aug 2026",
    travelTo: "14 Aug 2026",
    flightNo: "QR-460",
    airport: "Najaf International",
    arrivalTime: "04:25 PM",
    checkInTime: "05:30 PM",
    checkOutTime: "11:00 AM",
    chargeBasis: "Per Room",
    passengers: 19,
    passengerDetails: "2 families · 11 individual applicants",
    roomsRequested: 7,
    roomTypes: "5 Standard 4 People · 2 Standard 2 People",
    matchingRooms: 8,
    status: "Available",
  },
];

const transportModeByTour: Record<string, string> = {
  "Ziyarat Najaf 2026": "By Flight",
  "Miqaat Karbala 2026": "By Road",
  "Instant Najaf Group": "By Flight",
  "External Hotel Test Tour": "By Sea",
  "Family Shared Rooms Tour": "By Flight",
  "Ashara Karbala Arrival": "By Road",
  "London Walk-in Group": "By Flight",
};

const venueScheduleByTour: Record<
  string,
  {
    najafProperty: string;
    najafFrom: string;
    najafTo: string;
    karbalaProperty: string;
    karbalaFrom: string;
    karbalaTo: string;
  }
> = {
  "Ziyarat Najaf 2026": {
    najafProperty: "Faiz Hussainia",
    najafFrom: "07 Aug 2026",
    najafTo: "10 Aug 2026",
    karbalaProperty: "Al-Amin Hotel",
    karbalaFrom: "10 Aug 2026",
    karbalaTo: "14 Aug 2026",
  },
  "Miqaat Karbala 2026": {
    najafProperty: "Najaf Faiz",
    najafFrom: "07 Aug 2026",
    najafTo: "11 Aug 2026",
    karbalaProperty: "Al-Amin Hotel",
    karbalaFrom: "11 Aug 2026",
    karbalaTo: "17 Aug 2026",
  },
  "Instant Najaf Group": {
    najafProperty: "Grand Hotel Najaf",
    najafFrom: "08 Aug 2026",
    najafTo: "09 Aug 2026",
    karbalaProperty: "Al Kawthar Hotel",
    karbalaFrom: "09 Aug 2026",
    karbalaTo: "11 Aug 2026",
  },
  "External Hotel Test Tour": {
    najafProperty: "Grand Hotel Najaf",
    najafFrom: "08 Aug 2026",
    najafTo: "11 Aug 2026",
    karbalaProperty: "Al Kawthar Hotel",
    karbalaFrom: "11 Aug 2026",
    karbalaTo: "15 Aug 2026",
  },
  "Family Shared Rooms Tour": {
    najafProperty: "Najaf Faiz",
    najafFrom: "09 Aug 2026",
    najafTo: "12 Aug 2026",
    karbalaProperty: "Faiz Hussainia",
    karbalaFrom: "12 Aug 2026",
    karbalaTo: "16 Aug 2026",
  },
  "Ashara Karbala Arrival": {
    najafProperty: "Najaf Faiz",
    najafFrom: "10 Aug 2026",
    najafTo: "14 Aug 2026",
    karbalaProperty: "Faiz Hussainia",
    karbalaFrom: "14 Aug 2026",
    karbalaTo: "20 Aug 2026",
  },
  "London Walk-in Group": {
    najafProperty: "Grand Hotel Najaf",
    najafFrom: "11 Aug 2026",
    najafTo: "12 Aug 2026",
    karbalaProperty: "Al-Amin Hotel",
    karbalaFrom: "12 Aug 2026",
    karbalaTo: "14 Aug 2026",
  },
};

// const utilizationRows = [
//   {
//     date: "18 Sep 2026",
//     venue: "Karbala",
//     property: "Faiz Hussainia",
//     type: "Faiz",
//     rooms: 80,
//     occupied: 68,
//     pax: 156,
//     utilization: 85,
//     checkIns: 18,
//     checkOuts: 4,
//   },
//   {
//     date: "18 Sep 2026",
//     venue: "Karbala",
//     property: "Al Kawthar Hotel",
//     type: "Hotel",
//     rooms: 120,
//     occupied: 102,
//     pax: 224,
//     utilization: 85,
//     checkIns: 27,
//     checkOuts: 8,
//   },
//   {
//     date: "18 Sep 2026",
//     venue: "Najaf",
//     property: "Najaf Faiz",
//     type: "Faiz",
//     rooms: 64,
//     occupied: 56,
//     pax: 132,
//     utilization: 88,
//     checkIns: 14,
//     checkOuts: 6,
//   },
//   {
//     date: "19 Sep 2026",
//     venue: "Najaf",
//     property: "Grand Hotel Najaf",
//     type: "Hotel",
//     rooms: 95,
//     occupied: 74,
//     pax: 168,
//     utilization: 78,
//     checkIns: 9,
//     checkOuts: 21,
//   },
//   {
//     date: "19 Sep 2026",
//     venue: "Karbala",
//     property: "Al-Amin Hotel",
//     type: "Hotel",
//     rooms: 70,
//     occupied: 65,
//     pax: 146,
//     utilization: 93,
//     checkIns: 7,
//     checkOuts: 18,
//   },
// ];

const propertyUtilizationDetailRows = [
  {
    isoDate: "2026-08-07",
    date: "07 Aug 2026",
    venue: "Najaf",
    property: "Najaf Faiz",
    propertyType: "Faiz",
    operator: "Al Burhani Tours",
    tour: "Ziyarat Najaf 2026",
    stayFrom: "07 Aug 2026",
    stayTo: "14 Aug 2026",
    totalRooms: 64,
    occupiedRooms: 52,
    roomsRemaining: 12,
    roomsAllocatedToTour: 12,
    applicants: 42,
    utilization: 81,
    status: "Healthy capacity",
  },
  {
    isoDate: "2026-08-07",
    date: "07 Aug 2026",
    venue: "Najaf",
    property: "Najaf Faiz",
    propertyType: "Faiz",
    operator: "Burhani Family Tours",
    tour: "Family Shared Rooms Tour",
    stayFrom: "07 Aug 2026",
    stayTo: "13 Aug 2026",
    totalRooms: 64,
    occupiedRooms: 52,
    roomsRemaining: 12,
    roomsAllocatedToTour: 10,
    applicants: 31,
    utilization: 81,
    status: "Healthy capacity",
  },
  {
    isoDate: "2026-08-08",
    date: "08 Aug 2026",
    venue: "Karbala",
    property: "Al-Amin Hotel",
    propertyType: "Hotel",
    operator: "Saifee Tours & Travel",
    tour: "Miqaat Karbala 2026",
    stayFrom: "08 Aug 2026",
    stayTo: "17 Aug 2026",
    totalRooms: 70,
    occupiedRooms: 61,
    roomsRemaining: 9,
    roomsAllocatedToTour: 9,
    applicants: 28,
    utilization: 87,
    status: "Limited availability",
  },
  {
    isoDate: "2026-08-08",
    date: "08 Aug 2026",
    venue: "Karbala",
    property: "Al Kawthar Hotel",
    propertyType: "Hotel",
    operator: "External Hotel Operator",
    tour: "External Hotel Test Tour",
    stayFrom: "08 Aug 2026",
    stayTo: "15 Aug 2026",
    totalRooms: 120,
    occupiedRooms: 96,
    roomsRemaining: 24,
    roomsAllocatedToTour: 8,
    applicants: 22,
    utilization: 80,
    status: "Capacity available",
  },
  {
    isoDate: "2026-08-09",
    date: "09 Aug 2026",
    venue: "Najaf",
    property: "Grand Hotel Najaf",
    propertyType: "Hotel",
    operator: "Najaf Instant Tours",
    tour: "Instant Najaf Group",
    stayFrom: "09 Aug 2026",
    stayTo: "12 Aug 2026",
    totalRooms: 95,
    occupiedRooms: 70,
    roomsRemaining: 25,
    roomsAllocatedToTour: 6,
    applicants: 16,
    utilization: 74,
    status: "Capacity available",
  },
  {
    isoDate: "2026-08-10",
    date: "10 Aug 2026",
    venue: "Karbala",
    property: "Faiz Hussainia",
    propertyType: "Faiz",
    operator: "Dubai Ziyarat Group",
    tour: "Ashara Karbala Arrival",
    stayFrom: "10 Aug 2026",
    stayTo: "20 Aug 2026",
    totalRooms: 80,
    occupiedRooms: 74,
    roomsRemaining: 6,
    roomsAllocatedToTour: 13,
    applicants: 38,
    utilization: 93,
    status: "Near capacity",
  },
  {
    isoDate: "2026-08-11",
    date: "11 Aug 2026",
    venue: "Najaf",
    property: "Grand Hotel Najaf",
    propertyType: "Hotel",
    operator: "London Mumineen Group",
    tour: "London Walk-in Group",
    stayFrom: "11 Aug 2026",
    stayTo: "14 Aug 2026",
    totalRooms: 95,
    occupiedRooms: 78,
    roomsRemaining: 17,
    roomsAllocatedToTour: 7,
    applicants: 19,
    utilization: 82,
    status: "Healthy capacity",
  },
];

const reports = [
  {
    id: "property-wise",
    title: "Accommodation Utilization Report",
    description:
      "Venue and property utilization by Tour Operator, applicants housed, occupied rooms and rooms remaining",
    icon: <Building2 className="h-5 w-5" />,
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    category: "Properties",
    lastGenerated: "1 day ago",
    badge: null,
  },
  {
    id: "room-allocation",
    title: "Room Allocation Report",
    description:
      "Requested room types and applicant counts compared with actual property and room allocations",
    icon: <BedDouble className="h-5 w-5" />,
    color: "bg-indigo-50",
    iconColor: "text-indigo-600",
    category: "Operations",
    lastGenerated: "1 hour ago",
    badge: null,
  },
  {
    id: "checkin-checkout",
    title: "Check-in / Check-out Report",
    description:
      "Room-level planned and actual timings, early check-ins, late check-outs and charges",
    icon: <CheckCircle className="h-5 w-5" />,
    color: "bg-green-50",
    iconColor: "text-green-600",
    category: "Operations",
    lastGenerated: "30 min ago",
    badge: null,
  },
];

export function AccommodationReportsCheckInPage({
  onNavigate,
  initialReport,
}: AccommodationReportsPageProps) {
  const { setPageHeader } = usePageHeader();
  const [activeReport, setActiveReport] = useState<string | null>(
    initialReport ?? null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState(TODAY);
  const [dateTo, setDateTo] = useState(TODAY);
  const [selectedVenue, setSelectedVenue] = useState("All");
  const [venues, setVenues] = useState<VenueApi[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState("All");
  const [properties, setProperties] = useState<AllPropertyApi[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [dailySummary, setDailySummary] =
    useState<DailyCheckInSummaryStatsApi | null>(null);
  const [dailySummaryLoading, setDailySummaryLoading] = useState(false);
  const [arrivingSummary, setArrivingSummary] =
    useState<DailyCheckInReportTourApi[] | null>(null);
  const [arrivingSummaryLoading, setArrivingSummaryLoading] = useState(false);
  const [arrivingSummaryPage, setArrivingSummaryPage] = useState(1);
  const [arrivingSummaryPageSize, setArrivingSummaryPageSize] = useState(10);
  const [arrivingTourDetails, setArrivingTourDetails] =
    useState<DailyCheckInReportTourApi[] | null>(null);
  const [arrivingTourDetailsLoading, setArrivingTourDetailsLoading] =
    useState(false);
  const [tourFilterOptions, setTourFilterOptions] = useState<
    DailyCheckInTourFilterOptionApi[]
  >([]);
  const [tourFilterOptionsLoading, setTourFilterOptionsLoading] =
    useState(false);
  const [selectedCheckInTour, setSelectedCheckInTour] = useState("All");
  const [applicantPage, setApplicantPage] = useState(1);
  const [applicantsPerPage, setApplicantsPerPage] = useState(10);
  const [generatingReport, setGeneratingReport] = useState<string | null>(null);
  const [exportingReport, setExportingReport] = useState<
    "pdf" | "excel" | null
  >(null);
  const selectedVenueId =
    selectedVenue === "All"
      ? null
      : venues.find((venue) => venue.venueName === selectedVenue)?.venueId;
  const selectedVenuePublicId =
    selectedVenue === "All"
      ? undefined
      : venues.find((venue) => venue.venueName === selectedVenue)?.publicId;
  const selectedPropertyId =
    selectedProperty === "All"
      ? null
      : properties.find(
          (property) => property.propertyName.trim() === selectedProperty,
        )?.propertyId;

  const selectedPropertyPublicId =
    selectedProperty === "All"
      ? undefined
      : properties.find(
          (property) => property.propertyName.trim() === selectedProperty,
        )?.publicId;

  useEffect(() => {
    setPageHeader({
      title:
        initialReport === "occupancy"
          ? "Daily Check-in Report"
          : "Accommodation Reports",
      icon: <FileSpreadsheet className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [initialReport, setPageHeader]);

  
  //VENUE DROPDWON API RESPONE
  useEffect(() => {
    let isMounted = true;

    const loadVenues = async () => {
      try {
        setVenuesLoading(true);
        const venueList = await getVenues();
        if (isMounted) setVenues(venueList);
      } catch {
        if (isMounted) toast.error("Failed to load venues");
      } finally {
        if (isMounted) setVenuesLoading(false);
      }
    };

    loadVenues();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setArrivingSummaryPage(1);
  }, [dateFrom, dateTo, selectedVenue, selectedProperty, selectedCheckInTour]);

  // TOUR FILTER OPTIONS API RESPONSE
  useEffect(() => {
    if (activeReport !== "occupancy") {
      setTourFilterOptions([]);
      setTourFilterOptionsLoading(false);
      return;
    }

    if (
      (selectedVenue !== "All" && selectedVenueId == null) ||
      (selectedProperty !== "All" && selectedPropertyId == null)
    ) {
      setTourFilterOptions([]);
      setTourFilterOptionsLoading(false);
      return;
    }

    let isMounted = true;

    const loadTourFilterOptions = async () => {
      try {
        setTourFilterOptionsLoading(true);
        const options = await getDailyCheckInTourFilterOptions({
          venueId: selectedVenueId ?? undefined,
          propertyId: selectedPropertyId ?? undefined,
        });

        if (!isMounted) return;
        setTourFilterOptions(options);
        setSelectedCheckInTour((currentSelection) => {
          if (currentSelection === "All" || currentSelection === "AllDetails") {
            return currentSelection;
          }

          return options.some(
            (option) => `tour:${option.tourId}` === currentSelection,
          )
            ? currentSelection
            : "All";
        });
      } catch {
        if (isMounted) {
          setTourFilterOptions([]);
          toast.error("Failed to load arriving tour options");
        }
      } finally {
        if (isMounted) setTourFilterOptionsLoading(false);
      }
    };

    loadTourFilterOptions();

    return () => {
      isMounted = false;
    };
  }, [
    activeReport,
    selectedProperty,
    selectedPropertyId,
    selectedVenue,
    selectedVenueId,
  ]);

  useEffect(() => {
    if (
      activeReport !== "occupancy" ||
      selectedCheckInTour !== "All" ||
      !dateFrom ||
      !dateTo
    ) {
      setArrivingSummaryLoading(false);
      return;
    }

    if (
      (selectedVenue !== "All" && selectedVenuePublicId == null) ||
      (selectedProperty !== "All" && selectedPropertyPublicId == null)
    ) {
      setArrivingSummaryLoading(false);
      return;
    }

    let isMounted = true;

    const loadArrivingSummary = async () => {
      try {
        setArrivingSummaryLoading(true);
        const summary = await getDailyCheckInReport({
          startDate: `${dateFrom}T00:00:00`,
          endDate: `${dateTo}T23:59:59`,
          venuePublicId: selectedVenuePublicId,
          propertyPublicId: selectedPropertyPublicId,
          isDetails: false,
        });
        if (isMounted) setArrivingSummary(summary);
      } catch {
        if (isMounted) {
          setArrivingSummary(null);
          toast.error("Failed to load arriving tours summary");
        }
      } finally {
        if (isMounted) setArrivingSummaryLoading(false);
      }
    };

    loadArrivingSummary();

    return () => {
      isMounted = false;
    };
  }, [
    activeReport,
    dateFrom,
    dateTo,
    selectedCheckInTour,
    selectedProperty,
    selectedPropertyPublicId,
    selectedVenue,
    selectedVenuePublicId,
  ]);

  // DAILY TOUR DETAILS API RESPONSE
  useEffect(() => {
    if (
      activeReport !== "occupancy" ||
      selectedCheckInTour === "All" ||
      !dateFrom ||
      !dateTo
    ) {
      setArrivingTourDetails(null);
      setArrivingTourDetailsLoading(false);
      return;
    }

    if (
      (selectedVenue !== "All" && selectedVenuePublicId == null) ||
      (selectedProperty !== "All" && selectedPropertyPublicId == null)
    ) {
      setArrivingTourDetails(null);
      setArrivingTourDetailsLoading(false);
      return;
    }

    const isAllDetailsSelection = selectedCheckInTour === "AllDetails";
    const isSpecificTourSelection = selectedCheckInTour.startsWith("tour:");
    const specificTourId = isSpecificTourSelection
      ? Number(selectedCheckInTour.slice("tour:".length))
      : null;

    if (
      (!isAllDetailsSelection && !isSpecificTourSelection) ||
      (specificTourId !== null &&
        (!Number.isInteger(specificTourId) || specificTourId < 0))
    ) {
      setArrivingTourDetails(null);
      setArrivingTourDetailsLoading(false);
      return;
    }

    let isMounted = true;

    const loadArrivingTourDetails = async () => {
      try {
        setArrivingTourDetailsLoading(true);
        setArrivingTourDetails(null);
        const response = await getDailyCheckInReport({
          startDate: `${dateFrom}T00:00:00`,
          endDate: `${dateTo}T23:59:59`,
          venuePublicId: selectedVenuePublicId,
          propertyPublicId: selectedPropertyPublicId,
          isDetails: true,
          specificTourId: specificTourId ?? undefined,
        });

        if (isMounted) {
          setArrivingTourDetails(response);
        }
      } catch {
        if (isMounted) {
          setArrivingTourDetails(null);
          toast.error("Failed to load arriving tours details");
        }
      } finally {
        if (isMounted) setArrivingTourDetailsLoading(false);
      }
    };

    loadArrivingTourDetails();

    return () => {
      isMounted = false;
    };
  }, [
    activeReport,
    dateFrom,
    dateTo,
    selectedCheckInTour,
    selectedProperty,
    selectedPropertyPublicId,
    selectedVenue,
    selectedVenuePublicId,
  ]);

  // DAILY SUMMARY CARD API RESPONSE
  useEffect(() => {
    if (activeReport !== "occupancy" || !dateFrom) {
      setDailySummary(null);
      setDailySummaryLoading(false);
      return;
    }

    let isMounted = true;

    const loadDailySummary = async () => {
      try {
        setDailySummaryLoading(true);
        setDailySummary(null);
        const summary = await getDailyCheckInSummaryStats(dateFrom);
        if (isMounted) setDailySummary(summary);
      } catch {
        if (isMounted) {
          setDailySummary(null);
          toast.error("Failed to load daily check-in summary");
        }
      } finally {
        if (isMounted) setDailySummaryLoading(false);
      }
    };

    loadDailySummary();

    return () => {
      isMounted = false;
    };
  }, [activeReport, dateFrom]);

  //PROPERTIES DROPDOWN API RESPONE
  useEffect(() => {
    let isMounted = true;
    setProperties([]);

    const loadProperties = async () => {
      try {
        setPropertiesLoading(true);
        const propertyList = await getAllProperties(selectedVenuePublicId);
        if (isMounted) setProperties(propertyList);
      } catch {
        if (isMounted) toast.error("Failed to load properties");
      } finally {
        if (isMounted) setPropertiesLoading(false);
      }
    };

    loadProperties();

    return () => {
      isMounted = false;
    };
  }, [selectedVenue, selectedVenuePublicId]);

  useEffect(() => {
    setApplicantPage(1);
  }, [selectedCheckInTour, selectedVenue, selectedProperty, dateFrom, dateTo]);

  const filteredReports = reports.filter((r) => {
    const matchesSearch =
      r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const normalizedSelectedVenue = selectedVenue.trim().toLowerCase();

  const propertyOptions = Array.from(
    properties
      .reduce((options, property) => {
        const propertyName = property.propertyName.trim();
        const belongsToSelectedVenue =
          selectedVenue === "All" ||
          property.venueName.trim().toLowerCase() === normalizedSelectedVenue;

        if (property.isActive && propertyName && belongsToSelectedVenue) {
          options.set(propertyName.toLowerCase(), propertyName);
        }

        return options;
      }, new Map<string, string>())
      .values(),
  );

  const filteredDailyTourOptions = dailyTourAvailabilityRows.filter((row) => {
    const schedule = venueScheduleByTour[row.tour];
    const matchesProperty =
      selectedProperty === "All" ||
      (selectedVenue === "Najaf"
        ? schedule?.najafProperty === selectedProperty
        : selectedVenue === "Karbala"
          ? schedule?.karbalaProperty === selectedProperty
          : schedule?.najafProperty === selectedProperty ||
            schedule?.karbalaProperty === selectedProperty);
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !query ||
      row.tour.toLowerCase().includes(query) ||
      row.operator.toLowerCase().includes(query) ||
      row.flightNo.toLowerCase().includes(query);
    return (
      row.isoDate >= dateFrom &&
      row.isoDate <= dateTo &&
      matchesProperty &&
      matchesSearch
    );
  });
  const isAllArrivingSelection =
    selectedCheckInTour === "All" || selectedCheckInTour === "AllDetails";
  const activeDailyTourRows = filteredDailyTourOptions.filter(
    (row) => isAllArrivingSelection || row.tour === selectedCheckInTour,
  );
  const arrivingSummaryRows = (arrivingSummary ?? []).slice(
    (arrivingSummaryPage - 1) * arrivingSummaryPageSize,
    arrivingSummaryPage * arrivingSummaryPageSize,
  );
  const isAllDetailsSelection = selectedCheckInTour === "AllDetails";
  const selectedArrivingDetailsTourId = selectedCheckInTour.startsWith("tour:")
    ? Number(selectedCheckInTour.slice("tour:".length))
    : null;
  const selectedArrivingTourDetailHeader = arrivingTourDetails?.find(
    (tour) => tour.tourId === selectedArrivingDetailsTourId,
  );
  const allArrivingTourApplicants = (arrivingTourDetails ?? []).flatMap((tour) =>
    (tour.fullDetails?.applicants ?? []).map((applicant) => ({
      ...applicant,
      tour,
      age: getApplicantAge(applicant.dateOfBirth, tour.arrivalDate),
      applicantName: [applicant.firstName, applicant.lastName].filter(Boolean).join(" "),
    })),
  );
  const displayedArrivingTourApplicants = allArrivingTourApplicants.slice(
    (applicantPage - 1) * applicantsPerPage,
    applicantPage * applicantsPerPage,
  );
  const dailySummaryValue = (value?: number) =>
    dailySummaryLoading ? "…" : value == null ? "—" : String(value);

  const summaryStats =
    activeReport === "occupancy"
      ? [
          {
            label: "Tours Arriving Today",
            value: dailySummaryValue(dailySummary?.toursArrivingToday),
            sub: `Selected date · ${formatCardDate(dateFrom)}`,
            color: "text-blue-600",
            bg: "bg-blue-50",
          },
          {
            label: "Pax Arriving Today",
            value: dailySummaryValue(dailySummary?.totalApplicantsArriving),
            sub: "Total applicants arriving",
            color: "text-emerald-600",
            bg: "bg-emerald-50",
          },
          {
            label: "Rooms Requested Today",
            value: dailySummaryValue(dailySummary?.roomsRequestedToday),
            sub: dailySummaryLoading
              ? "Loading room breakdown..."
              : dailySummary?.roomBreakdown?.trim() || "—",
            color: "text-amber-600",
            bg: "bg-amber-50",
          },
        ]
      : [
          {
            label: "Total Rooms",
            value: "485",
            sub: "Across all properties",
            color: "text-blue-600",
            bg: "bg-blue-50",
          },
          {
            label: "Occupied",
            value: "421",
            sub: "86.8% occupancy rate",
            color: "text-green-600",
            bg: "bg-green-50",
          },
          {
            label: "Vacant",
            value: "64",
            sub: "13.2% availability",
            color: "text-amber-600",
            bg: "bg-amber-50",
          },
        ];

  const activeCheckInOutRows = checkInOutDetailRows.filter(
    (row) =>
      row.isoDate >= dateFrom &&
      row.isoDate <= dateTo &&
      (selectedVenue === "All" || row.venue === selectedVenue) &&
      (selectedProperty === "All" || row.property === selectedProperty),
  );
  const activeCheckInOutChartData = Array.from(
    new Set(activeCheckInOutRows.map((row) => row.isoDate)),
  )
    .sort()
    .map((isoDate) => {
      const rows = activeCheckInOutRows.filter(
        (row) => row.isoDate === isoDate,
      );
      return {
        date: rows[0]?.date.replace(" 2026", "") ?? isoDate,
        checkIn: rows
          .filter((row) => row.actualCheckIn !== "—")
          .reduce((sum, row) => sum + row.pax, 0),
        checkOut: rows
          .filter((row) => row.actualCheckOut !== "—")
          .reduce((sum, row) => sum + row.pax, 0),
      };
    });
  const activeRoomAllocationRows = roomAllocationReportRows.filter(
    (row) =>
      row.isoDate >= dateFrom &&
      row.isoDate <= dateTo &&
      (selectedVenue === "All" || row.venue === selectedVenue) &&
      (selectedProperty === "All" ||
        row.requestedProperty === selectedProperty ||
        row.allocatedProperty === selectedProperty),
  );
  const roomAllocationChartData = Array.from(
    new Set(activeRoomAllocationRows.map((row) => row.tour)),
  ).map((tour) => {
    const rows = activeRoomAllocationRows.filter((row) => row.tour === tour);
    return {
      tour: tour.length > 20 ? `${tour.slice(0, 18)}…` : tour,
      requestedRooms: rows.reduce((sum, row) => sum + row.requestedRooms, 0),
      allocatedRooms: rows.reduce(
        (sum, row) => sum + row.allocatedRoomCount,
        0,
      ),
      requestedApplicants: rows.reduce(
        (sum, row) => sum + row.requestedApplicants,
        0,
      ),
      allocatedApplicants: rows.reduce(
        (sum, row) => sum + row.allocatedApplicants,
        0,
      ),
    };
  });
  const activePropertyUtilizationRows = propertyUtilizationDetailRows.filter(
    (row) =>
      row.isoDate >= dateFrom &&
      row.isoDate <= dateTo &&
      (selectedVenue === "All" || row.venue === selectedVenue) &&
      (selectedProperty === "All" || row.property === selectedProperty),
  );
  const propertyUtilizationChartData = Array.from(
    new Set(activePropertyUtilizationRows.map((row) => row.property)),
  ).map((property) => {
    const rows = activePropertyUtilizationRows.filter(
      (row) => row.property === property,
    );
    const inventory = rows[0];
    return {
      property,
      occupiedRooms: inventory?.occupiedRooms ?? 0,
      roomsRemaining: inventory?.roomsRemaining ?? 0,
      applicants: rows.reduce((sum, row) => sum + row.applicants, 0),
      utilization: inventory?.utilization ?? 0,
    };
  });

  const getDailyCheckInExportPayload = (): DailyCheckInExportRequest | null => {
    if (
      !dateFrom ||
      !dateTo ||
      dateFrom > dateTo ||
      (selectedVenue !== "All" && selectedVenueId == null) ||
      (selectedProperty !== "All" && selectedPropertyId == null)
    ) {
      return null;
    }

    const specificTourId = selectedCheckInTour.startsWith("tour:")
      ? Number(selectedCheckInTour.slice("tour:".length))
      : null;
    if (
      specificTourId !== null &&
      (!Number.isInteger(specificTourId) || specificTourId < 0)
    ) {
      return null;
    }

    return {
      fromDate: `${dateFrom}T00:00:00`,
      toDate: `${dateTo}T23:59:59`,
      venueIds: selectedVenueId == null ? [] : [selectedVenueId],
      propertyIds: selectedPropertyId == null ? [] : [selectedPropertyId],
      specificTourId,
      pageNumber: 0,
      pageSize: 0,
    };
  };

  const downloadExportFile = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  };

  const handlePrintReport = async () => {
    const payload = getDailyCheckInExportPayload();
    if (!payload) {
      toast.error("Select a valid date range, venue, property and tour");
      return;
    }

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.title = "Preparing daily check-in PDF";
      printWindow.document.body.textContent = "Preparing report…";
    }

    try {
      setExportingReport("pdf");
      const file = await exportDailyCheckInPdf(payload);
      const url = URL.createObjectURL(file.blob);

      if (printWindow && !printWindow.closed) {
        printWindow.addEventListener(
          "load",
          () => {
            printWindow.focus();
            printWindow.print();
          },
          { once: true },
        );
        printWindow.location.replace(url);
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        downloadExportFile(file.blob, file.fileName);
        URL.revokeObjectURL(url);
      }

      toast.success("Daily check-in PDF generated successfully");
    } catch (error) {
      printWindow?.close();
      console.error("Failed to export daily check-in PDF:", error);
      toast.error("Failed to generate daily check-in PDF");
    } finally {
      setExportingReport(null);
    }
  };

  const handleExcelReport = async () => {
    const payload = getDailyCheckInExportPayload();
    if (!payload) {
      toast.error("Select a valid date range, venue, property and tour");
      return;
    }

    try {
      setExportingReport("excel");
      const file = await exportDailyCheckInExcel(payload);
      downloadExportFile(file.blob, file.fileName);
      toast.success("Daily check-in Excel exported successfully");
    } catch (error) {
      console.error("Failed to export daily check-in Excel:", error);
      toast.error("Failed to export daily check-in Excel");
    } finally {
      setExportingReport(null);
    }
  };

  const handleGenerate = (reportId: string) => {
    setGeneratingReport(reportId);
    setTimeout(() => {
      setGeneratingReport(null);
      setActiveReport(reportId);
    }, 1200);
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        {/* Summary Stats */}
        {(venuesLoading ||
          propertiesLoading ||
          dailySummaryLoading ||
          arrivingSummaryLoading ||
          arrivingTourDetailsLoading) && <FullScreenLoading />}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {summaryStats.map((stat) => (
            <Card key={stat.label} className="bg-white border-0 shadow-sm">
              <CardContent className="flex items-center gap-3 p-3">
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${stat.bg}`}
                >
                  <BarChart3 className={`h-4 w-4 ${stat.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-gray-900">
                    {stat.label}
                  </div>
                  <div className="mt-0.5 text-[10px] text-gray-500">
                    {stat.sub}
                  </div>
                </div>
                <div className={`shrink-0 text-lg font-bold ${stat.color}`}>
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center gap-4 overflow-x-visible md:flex-nowrap md:overflow-x-auto">
              <div className="hidden relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={
                    activeReport === "occupancy"
                      ? "Search tour, Tour Operator or flight..."
                      : "Search reports..."
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 pl-9 text-xs"
                />
              </div>
              <div className="flex min-w-0 w-full shrink flex-wrap items-center gap-4 [&>*]:max-w-full md:w-auto md:shrink-0 md:flex-nowrap md:[&>*]:max-w-none">
                {/* <Calendar className="h-4 w-4 text-gray-400" /> */}
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 rounded-md border border-gray-300 px-2 text-xs"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 rounded-md border border-gray-300 px-2 text-xs"
                />
                <Select
                  value={selectedVenue}
                  onValueChange={(value) => {
                    setSelectedVenue(value);
                    setSelectedProperty("All");
                  }}
                  disabled={venuesLoading}
                >
                  <SelectTrigger
                    className="h-8 border-gray-200 text-xs"
                    style={{ width: 220, flexShrink: 0 }}
                  >
                    <SelectValue
                      placeholder={
                        venuesLoading ? "Loading venues..." : "All Venue Stays"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Venue Stays</SelectItem>
                    {venues.map((venue) => (
                      <SelectItem key={venue.venueId} value={venue.venueName}>
                        {venue.venueName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={selectedProperty}
                  onValueChange={setSelectedProperty}
                  disabled={propertiesLoading}
                >
                  <SelectTrigger
                    className="h-8 border-gray-200 text-xs"
                    style={{ width: 220, flexShrink: 0 }}
                  >
                    <SelectValue
                      placeholder={
                        propertiesLoading
                          ? "Loading properties..."
                          : "All Properties"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Properties</SelectItem>
                    {propertyOptions.map((property) => (
                      <SelectItem key={property} value={property}>
                        {property}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activeReport === "occupancy" && (
                  <div style={{ width: 220, flexShrink: 0 }}>
                    <Select
                      value={selectedCheckInTour}
                      onValueChange={setSelectedCheckInTour}
                      disabled={tourFilterOptionsLoading}
                    >
                      <SelectTrigger
                        className="h-8 w-full border-gray-200 text-xs"
                        aria-label="Select tour"
                      >
                        <SelectValue placeholder="All Arriving Summary" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[50vh]">
                        <SelectItem value="All">All Arriving Summary</SelectItem>
                        <SelectItem value="AllDetails">
                          All Arriving Details
                        </SelectItem>
                        {tourFilterOptions.map((option) => (
                          <SelectItem
                            key={option.tourId}
                            value={`tour:${option.tourId}`}
                          >
                            {option.tourName} · {option.officeName} ·{" "}
                            {option.noOfApplicants} applicants
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {activeReport && (
                <div className="ml-0 flex min-w-0 w-full shrink flex-wrap items-center gap-2 md:ml-auto md:w-auto md:shrink-0 md:flex-nowrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    onClick={() => {
                      const today = getLocalDateInputValue(new Date());
                      setDateFrom(today);
                      setDateTo(today);
                      setSelectedVenue("All");
                      setSelectedProperty("All");
                      setSelectedCheckInTour("All");
                      setSearchQuery("");
                      setArrivingSummaryPage(1);
                      setApplicantPage(1);
                    }}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Reset Filters
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    onClick={handlePrintReport}
                    disabled={exportingReport !== null}
                  >
                    {exportingReport === "pdf" ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Printer className="h-3.5 w-3.5" />
                    )}
                    {exportingReport === "pdf" ? "Preparing..." : "Print"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    onClick={handleExcelReport}
                    disabled={exportingReport !== null}
                  >
                    {exportingReport === "excel" ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    {exportingReport === "excel" ? "Exporting..." : "Excel"}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Charts */}
        {activeReport === null && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  Occupancy Trend (%) — Najaf vs Karbala
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={occupancyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} domain={[60, 100]} />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="karbala"
                      stroke="#5B9BD5"
                      strokeWidth={2}
                      dot={false}
                      name="Karbala"
                    />
                    <Line
                      type="monotone"
                      dataKey="najaf"
                      stroke="#10B981"
                      strokeWidth={2}
                      dot={false}
                      name="Najaf"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  Room Type Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <ResponsiveContainer width="100%" height={200}>
                  <RechartsPie>
                    <Pie
                      data={roomTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${value}%`}
                      labelLine={false}
                    >
                      {roomTypeData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">
                  {roomTypeData.map((d, i) => (
                    <div
                      key={d.name}
                      className="flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-1.5">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ background: COLORS[i] }}
                        />
                        <span className="text-gray-600">{d.name}</span>
                      </div>
                      <span className="font-medium">{d.value}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Active Report View */}
        {(activeReport === "occupancy" || activeReport === "property-wise") && (
          <Card className="bg-white border-0 shadow-sm">
            {activeReport === "property-wise" && (
              <CardHeader className="border-b px-4 py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm lg:text-base">
                    {activeReport === "property-wise"
                      ? "Accommodation Utilization — Faiz vs Hotels"
                      : "Daily Check-in Report"}{" "}
                    — {dateFrom} to {dateTo}
                  </CardTitle>
                </div>
              </CardHeader>
            )}
            <CardContent className="p-3">
              {activeReport === "property-wise" && (
                <div className="mb-3 grid gap-2 sm:grid-cols-4">
                  {[
                    {
                      label: "Properties Shown",
                      value: propertyUtilizationChartData.length,
                      tone: "bg-sky-50 text-sky-700",
                    },
                    {
                      label: "Applicants Housed",
                      value: propertyUtilizationChartData.reduce(
                        (sum, row) => sum + row.applicants,
                        0,
                      ),
                      tone: "bg-violet-50 text-violet-700",
                    },
                    {
                      label: "Occupied Rooms",
                      value: propertyUtilizationChartData.reduce(
                        (sum, row) => sum + row.occupiedRooms,
                        0,
                      ),
                      tone: "bg-emerald-50 text-emerald-700",
                    },
                    {
                      label: "Rooms Remaining",
                      value: propertyUtilizationChartData.reduce(
                        (sum, row) => sum + row.roomsRemaining,
                        0,
                      ),
                      tone: "bg-amber-50 text-amber-700",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className={`flex items-center justify-between rounded-lg px-3 py-2 ${item.tone}`}
                    >
                      <span className="text-[10px] font-medium">
                        {item.label}
                      </span>
                      <strong className="text-base">{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
              {activeReport === "property-wise" && (
                <div className="mb-3">
                  <ResponsiveContainer width="100%" height={190}>
                    <BarChart data={propertyUtilizationChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="property"
                        tick={{ fontSize: 10 }}
                        interval={0}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="occupiedRooms"
                        fill="#10B981"
                        name="Occupied Rooms"
                        radius={[3, 3, 0, 0]}
                      />
                      <Bar
                        dataKey="roomsRemaining"
                        fill="#5B9BD5"
                        name="Rooms Remaining"
                        radius={[3, 3, 0, 0]}
                      />
                      <Bar
                        dataKey="applicants"
                        fill="#8B5CF6"
                        name="Applicants Housed"
                        radius={[3, 3, 0, 0]}
                      />
                      <Bar
                        dataKey="utilization"
                        fill="#F59E0B"
                        name="Utilization %"
                        radius={[3, 3, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {activeReport === "occupancy" ? (
                selectedCheckInTour !== "All" ? (
                  <div className="space-y-3">
                    {selectedArrivingTourDetailHeader && (
                      <ArrivingTourDetailsHeaderCard
                        report={selectedArrivingTourDetailHeader}
                        selectedVenue={selectedVenue}
                      />
                    )}
                    <div className="rounded-lg border border-slate-100 bg-white">
                      <div className="overflow-x-auto">
                        <table
                          className={`w-full table-fixed text-sm ${isAllDetailsSelection ? "min-w-[1040px]" : "min-w-[840px]"}`}
                        >
                          <colgroup>
                            {isAllDetailsSelection && <col />}
                            <col />
                            <col />
                            <col />
                            <col className="w-[536px]" />
                          </colgroup>
                          <thead>
                            <tr className="border-b bg-sky-50/70">
                              {isAllDetailsSelection && (
                                <th className="whitespace-nowrap px-3 py-1.5 text-left text-[10px] font-bold text-sky-800">
                                  Tour / Operator
                                </th>
                              )}
                              <th className="whitespace-nowrap px-2 py-1.5 text-left text-[10px] font-bold text-sky-800">
                                Applicant / ITS
                              </th>
                              <th className="whitespace-nowrap px-2 py-1.5 text-left text-[10px] font-bold text-sky-800">
                                Gender / Age
                              </th>
                              <th className="whitespace-nowrap px-2 py-1.5 text-left text-[10px] font-bold text-sky-800">
                                Special Request
                              </th>
                              <th className="whitespace-nowrap px-2 py-1.5 text-left text-[10px] font-bold text-sky-800">
                                Stay / Room
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedArrivingTourApplicants.map(
                              (applicant) => {
                                const tourHeader = applicant.tour;
                                const stays = addApplicantRoomsToStays(
                                  mapReportStays(tourHeader.stayDetails).filter(
                                    (stay) => selectedVenue === "All" || stay.venue === selectedVenue,
                                  ),
                                  tourHeader.fullDetails?.tour.roomAllocations ?? [],
                                  applicant.visaApplicationId,
                                );

                                return (
                                  <tr
                                    key={`${applicant.tour.tourId}:${applicant.visaApplicationId}`}
                                    className="border-b hover:bg-sky-50/30"
                                  >
                                    {isAllDetailsSelection && (
                                      <td className="break-words px-3 py-1.5 align-top">
                                        <div className="text-[10px] font-semibold text-slate-800">
                                          {tourHeader?.tourName || "—"}
                                        </div>
                                        <div className="text-[9px] text-slate-500">
                                          {tourHeader?.tourOperatorName || "—"}
                                        </div>
                                      </td>
                                    )}
                                    <td className="break-words px-2 py-1.5 align-top leading-tight">
                                      <div className="text-[10px] font-bold text-slate-800">
                                        {applicant.applicantName || "—"}
                                      </div>
                                      <div className="mt-0.5 text-[9px] font-semibold text-slate-500">
                                        ITS {applicant.itsNo || "—"}
                                      </div>
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-1.5 align-top text-[10px] font-bold leading-tight text-slate-800">
                                      {applicant.gender || "—"} ·{" "}
                                      {applicant.age == null
                                        ? "—"
                                        : `${applicant.age}y`}
                                    </td>
                                    <td className="px-2 py-1.5 align-top text-[10px] font-bold leading-tight text-slate-800">
                                      {tourHeader.fullDetails?.tour.specialAccommodationRequest || "—"}
                                    </td>
                                    <td className="px-2 py-1.5 align-top text-[10px] text-slate-600">
                                      <StayDetailsBadges stays={stays} />
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                            {!displayedArrivingTourApplicants.length && (
                              <tr>
                                <td
                                  colSpan={isAllDetailsSelection ? 5 : 4}
                                  className="py-10 text-center text-xs text-slate-500"
                                >
                                  No arriving tour details match the selected
                                  filters.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {arrivingTourDetails != null &&
                        allArrivingTourApplicants.length > 0 && (
                          <Pagination
                            totalItems={allArrivingTourApplicants.length}
                            itemsPerPage={applicantsPerPage}
                            currentPage={applicantPage}
                            onPageChange={setApplicantPage}
                            onPageSizeChange={(size) => {
                              setApplicantsPerPage(size);
                              setApplicantPage(1);
                            }}
                            pageSizeOptions={[10, 20, 50, 100]}
                            label="applicants"
                          />
                        )}
                    </div>
                  </div>
                ) : (
                  // final else: show arriving summary table
                  <div className="rounded-lg border border-slate-100">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[1040px] table-fixed break-words text-sm">
                        <colgroup>
                          <col />
                          <col />
                          <col />
                          <col />
                          <col className="w-[544px]" />
                        </colgroup>
                        <thead>
                          <tr className="border-b bg-sky-50/70">
                            <th className="px-3 py-2 text-left text-[10px] text-sky-800">
                              Tour / Operator
                            </th>
                            <th className="px-3 py-2 text-left text-[10px] text-sky-800">
                              Travel Date & Time
                            </th>
                            <th className="px-3 py-2 text-left text-[10px] text-sky-800">
                              Travel Means
                            </th>
                            <th className="px-3 py-2 text-left text-[10px] text-sky-800">
                              Applicants
                            </th>
                            <th className="px-3 py-2 text-left text-[10px] text-sky-800">
                              Stay Details
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCheckInTour === "All"
                            ? arrivingSummaryRows.map((row) => {
                                const stays = mapReportStays(
                                  row.stayDetails,
                                );
                                const displayedStays = stays.filter(
                                  (stay) =>
                                    selectedVenue === "All" ||
                                    stay.venue === selectedVenue,
                                );
                                return (
                                  <tr key={row.tourId} className="border-b">
                                    <td className="px-3 py-2">
                                      <div className="text-[10px] font-semibold text-slate-800">
                                        {row.tourName}
                                      </div>
                                      <div className="text-[9px] text-slate-500">
                                        {row.tourOperatorName} ·{" "}
                                        {row.tourCategoryLabel || row.tourTypeLabel || "—"}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="text-[10px] font-semibold text-slate-700">
                                        {formatReportDate(row.arrivalDate)} ·{" "}
                                        {formatReportTimes(row.arrivalTime ?? "")}
                                      </div>
                                      <div className="text-[9px] text-slate-500">
                                        {row.arrivalFlightNo || "—"} ·{" "}
                                        {row.arrivalPortName || "—"}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      <Badge
                                        variant="outline"
                                        className="text-[9px] bg-blue-50 text-blue-700"
                                      >
                                        {row.travelMeans || "—"}
                                      </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs font-bold text-emerald-700">
                                      {row.noOfApplicants}
                                    </td>
                                    <td className="px-3 py-2">
                                      <StayDetailsBadges
                                        stays={displayedStays}
                                      />
                                    </td>
                                  </tr>
                                );
                              })
                            : activeDailyTourRows.map((row) => {
                                const schedule = venueScheduleByTour[row.tour];
                                const stays = sortAccommodationStays([
                                  {
                                    venue: "Najaf",
                                    property: schedule?.najafProperty || "—",
                                    fromDate: schedule?.najafFrom || "",
                                    toDate: schedule?.najafTo || "",
                                  },
                                  {
                                    venue: "Karbala",
                                    property: schedule?.karbalaProperty || "—",
                                    fromDate: schedule?.karbalaFrom || "",
                                    toDate: schedule?.karbalaTo || "",
                                  },
                                ]);
                                const displayedStays = stays.filter(
                                  (stay) =>
                                    selectedVenue === "All" ||
                                    stay.venue === selectedVenue,
                                );
                                return (
                                  <tr
                                    key={row.tour}
                                    className="cursor-pointer border-b hover:bg-sky-50/40"
                                    onClick={() =>
                                      setSelectedCheckInTour(row.tour)
                                    }
                                  >
                                    <td className="px-3 py-2">
                                      <div className="text-[10px] font-semibold text-slate-800">
                                        {row.tour}
                                      </div>
                                      <div className="text-[9px] text-slate-500">
                                        {row.operator} · {row.tourType}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="text-[10px] font-semibold text-slate-700">
                                        {row.date} · {row.arrivalTime}
                                      </div>
                                      <div className="text-[9px] text-slate-500">
                                        {row.flightNo} · {row.airport}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      <Badge
                                        variant="outline"
                                        className="text-[9px]"
                                      >
                                        {transportModeByTour[row.tour] ??
                                          "By Flight"}
                                      </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs font-bold text-emerald-700">
                                      {row.passengers}
                                    </td>
                                    <td className="px-3 py-2">
                                      <StayDetailsBadges
                                        stays={displayedStays}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                          {(selectedCheckInTour === "All"
                            ? !arrivingSummaryRows.length
                            : !activeDailyTourRows.length) && (
                            <tr>
                              <td
                                colSpan={5}
                                className="py-10 text-center text-xs text-slate-500"
                              >
                                No arriving tours match the selected filters.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {selectedCheckInTour === "All" &&
                      arrivingSummary != null &&
                      arrivingSummary.length > 0 && (
                        <Pagination
                          totalItems={arrivingSummary.length}
                          itemsPerPage={arrivingSummaryPageSize}
                          currentPage={arrivingSummaryPage}
                          onPageChange={setArrivingSummaryPage}
                          onPageSizeChange={(size) => {
                            setArrivingSummaryPageSize(size);
                            setArrivingSummaryPage(1);
                          }}
                          pageSizeOptions={[10, 20, 50, 100]}
                          label="tours"
                        />
                      )}
                  </div>
                )
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-100">
                  <table className="w-full min-w-[1680px] text-sm">
                    <thead>
                      <tr className="border-b bg-amber-50/60">
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Date
                        </th>
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Venue
                        </th>
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Property
                        </th>
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Property Type
                        </th>
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Tour Operator
                        </th>
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Tour
                        </th>
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Stay Dates
                        </th>
                        <th className="px-2 py-2 text-center text-[10px] text-amber-900">
                          Applicants
                        </th>
                        <th className="px-2 py-2 text-center text-[10px] text-amber-900">
                          Rooms for Tour
                        </th>
                        <th className="px-2 py-2 text-center text-[10px] text-amber-900">
                          Property Rooms
                        </th>
                        <th className="px-2 py-2 text-center text-[10px] text-amber-900">
                          Occupied
                        </th>
                        <th className="px-2 py-2 text-center text-[10px] text-amber-900">
                          Remaining
                        </th>
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Utilization
                        </th>
                        <th className="px-2 py-2 text-left text-[10px] text-amber-900">
                          Capacity Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activePropertyUtilizationRows.map((row, index) => (
                        <tr
                          key={`${row.isoDate}-${row.property}-${row.operator}-${index}`}
                          className="border-b hover:bg-amber-50/30"
                        >
                          <td className="whitespace-nowrap px-2 py-2 text-[10px] font-medium text-slate-700">
                            {row.date}
                          </td>
                          <td className="px-2 py-2 text-[10px] text-slate-700">
                            {row.venue}
                          </td>
                          <td className="px-2 py-2 text-[10px] font-semibold text-slate-800">
                            {row.property}
                          </td>
                          <td className="px-2 py-2">
                            <Badge
                              className={
                                row.propertyType === "Faiz"
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-blue-50 text-blue-700"
                              }
                            >
                              {row.propertyType}
                            </Badge>
                          </td>
                          <td className="px-2 py-2 text-[10px] font-semibold text-slate-800">
                            {row.operator}
                          </td>
                          <td className="px-2 py-2 text-[10px] text-slate-600">
                            {row.tour}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-[10px] text-slate-600">
                            {row.stayFrom} → {row.stayTo}
                          </td>
                          <td className="px-2 py-2 text-center text-xs font-bold text-violet-700">
                            {row.applicants}
                          </td>
                          <td className="px-2 py-2 text-center text-xs font-bold text-sky-700">
                            {row.roomsAllocatedToTour}
                          </td>
                          <td className="px-2 py-2 text-center text-[10px] font-semibold">
                            {row.totalRooms}
                          </td>
                          <td className="px-2 py-2 text-center text-xs font-bold text-emerald-700">
                            {row.occupiedRooms}
                          </td>
                          <td className="px-2 py-2 text-center text-xs font-bold text-blue-700">
                            {row.roomsRemaining}
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex min-w-[120px] items-center gap-2">
                              <div className="h-1.5 flex-1 rounded-full bg-slate-100">
                                <div
                                  className={`h-1.5 rounded-full ${row.utilization >= 90 ? "bg-rose-500" : row.utilization >= 75 ? "bg-amber-500" : "bg-emerald-500"}`}
                                  style={{ width: `${row.utilization}%` }}
                                />
                              </div>
                              <span className="text-[10px] font-bold text-slate-700">
                                {row.utilization}%
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <Badge
                              variant="outline"
                              className={
                                row.status === "Near capacity"
                                  ? "border-rose-200 bg-rose-50 text-rose-700"
                                  : row.status === "Limited availability"
                                    ? "border-amber-200 bg-amber-50 text-amber-700"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-700"
                              }
                            >
                              {row.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                      {!activePropertyUtilizationRows.length && (
                        <tr>
                          <td
                            colSpan={14}
                            className="py-10 text-center text-xs text-slate-500"
                          >
                            No property utilization matches the selected date,
                            venue and property filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeReport === "room-allocation" && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="border-b px-4 py-3">
              <CardTitle className="text-base">
                Room Allocation Report — Requested vs Allocated
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 p-3">
              <div className="grid gap-2 sm:grid-cols-4">
                {[
                  {
                    label: "Room Requests",
                    value: activeRoomAllocationRows.length,
                    tone: "text-sky-700 bg-sky-50",
                  },
                  {
                    label: "Applicants Requested",
                    value: activeRoomAllocationRows.reduce(
                      (sum, row) => sum + row.requestedApplicants,
                      0,
                    ),
                    tone: "text-violet-700 bg-violet-50",
                  },
                  {
                    label: "Applicants Allocated",
                    value: activeRoomAllocationRows.reduce(
                      (sum, row) => sum + row.allocatedApplicants,
                      0,
                    ),
                    tone: "text-emerald-700 bg-emerald-50",
                  },
                  {
                    label: "Pending / Partial",
                    value: activeRoomAllocationRows.filter(
                      (row) =>
                        row.status === "Pending" || row.status === "Partial",
                    ).length,
                    tone: "text-amber-700 bg-amber-50",
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 ${item.tone}`}
                  >
                    <span className="text-[10px] font-medium">
                      {item.label}
                    </span>
                    <strong className="text-base">{item.value}</strong>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-slate-100 bg-white p-2">
                <div className="mb-1 px-2 text-[11px] font-semibold text-slate-700">
                  Requested vs Allocated by Tour
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={roomAllocationChartData}
                    margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="tour" tick={{ fontSize: 9 }} interval={0} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar
                      dataKey="requestedRooms"
                      fill="#F59E0B"
                      name="Rooms Requested"
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="allocatedRooms"
                      fill="#5B9BD5"
                      name="Rooms Allocated"
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="requestedApplicants"
                      fill="#8B5CF6"
                      name="Applicants Requested"
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="allocatedApplicants"
                      fill="#10B981"
                      name="Applicants Allocated"
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-100">
                <table className="w-full min-w-[1880px] text-sm">
                  <thead>
                    <tr className="border-b bg-sky-50/70">
                      <th className="px-2 py-2 text-left text-[10px] text-sky-800">
                        Request
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-sky-800">
                        Tour Operator / Tour
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-sky-800">
                        Venue / Stay Dates
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-sky-800">
                        Requested Property
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-sky-800">
                        Requested Room Type
                      </th>
                      <th className="px-2 py-2 text-center text-[10px] text-sky-800">
                        Rooms Requested
                      </th>
                      <th className="px-2 py-2 text-center text-[10px] text-sky-800">
                        Applicants Requested
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-emerald-800">
                        Allocated Property
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-emerald-800">
                        Room Numbers
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-emerald-800">
                        Allocated Room Type
                      </th>
                      <th className="px-2 py-2 text-center text-[10px] text-emerald-800">
                        Applicants Allocated
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Allocated By / Date
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Status
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Variance / Remarks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRoomAllocationRows.map((row) => (
                      <tr
                        key={row.requestId}
                        className="border-b hover:bg-sky-50/30"
                      >
                        <td className="px-2 py-2">
                          <div className="text-[10px] font-semibold text-sky-800">
                            {row.requestId}
                          </div>
                          <div className="text-[9px] text-slate-400">
                            {row.requestedOn}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-[10px] font-semibold text-slate-800">
                            {row.operator}
                          </div>
                          <div className="text-[9px] text-slate-500">
                            {row.tour}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-[10px] font-medium text-slate-700">
                            {row.venue}
                          </div>
                          <div className="whitespace-nowrap text-[9px] text-slate-500">
                            {row.stayFrom} → {row.stayTo}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-[10px] text-slate-700">
                          {row.requestedProperty}
                        </td>
                        <td className="px-2 py-2">
                          <Badge variant="outline" className="text-[9px]">
                            {row.requestedType}
                          </Badge>
                        </td>
                        <td className="px-2 py-2 text-center text-xs font-bold text-amber-700">
                          {row.requestedRooms}
                        </td>
                        <td className="px-2 py-2 text-center text-xs font-bold text-violet-700">
                          {row.requestedApplicants}
                        </td>
                        <td className="px-2 py-2 text-[10px] font-medium text-emerald-800">
                          {row.allocatedProperty}
                        </td>
                        <td className="px-2 py-2 text-[10px] font-bold text-sky-800">
                          {row.allocatedRooms}
                        </td>
                        <td className="px-2 py-2 text-[10px] text-slate-700">
                          {row.allocatedType}
                        </td>
                        <td className="px-2 py-2 text-center text-xs font-bold text-emerald-700">
                          {row.allocatedApplicants}
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-[10px] text-slate-700">
                            {row.allocatedBy}
                          </div>
                          <div className="text-[9px] text-slate-400">
                            {row.allocatedOn}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <Badge
                            className={
                              row.status === "Completed"
                                ? "bg-emerald-50 text-emerald-700"
                                : row.status === "Reallocated"
                                  ? "bg-violet-50 text-violet-700"
                                  : row.status === "Partial"
                                    ? "bg-amber-50 text-amber-700"
                                    : "bg-rose-50 text-rose-700"
                            }
                          >
                            {row.status}
                          </Badge>
                        </td>
                        <td className="max-w-[240px] px-2 py-2 text-[10px] text-slate-600">
                          {row.variance}
                        </td>
                      </tr>
                    ))}
                    {!activeRoomAllocationRows.length && (
                      <tr>
                        <td
                          colSpan={14}
                          className="py-10 text-center text-xs text-slate-500"
                        >
                          No room allocations match the selected date, venue and
                          property filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeReport === "checkin-checkout" && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  Check-in / Check-out Report
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 p-4">
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={activeCheckInOutChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="checkIn"
                    fill="#10B981"
                    name="Check-in"
                    radius={[3, 3, 0, 0]}
                  />
                  <Bar
                    dataKey="checkOut"
                    fill="#EF4444"
                    name="Check-out"
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="overflow-x-auto rounded-lg border border-slate-100">
                <table className="w-full min-w-[1560px] text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Date
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Venue / Property
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Room No.
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Tour / Operator
                      </th>
                      <th className="px-2 py-2 text-center text-[10px] text-slate-600">
                        Passengers
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-emerald-700">
                        Planned Check-in
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-emerald-700">
                        Actual Check-in
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-amber-700">
                        Early Check-in
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-rose-600">
                        Planned Check-out
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-rose-600">
                        Actual Check-out
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-amber-700">
                        Late Check-out
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Movement Status
                      </th>
                      <th className="px-2 py-2 text-left text-[10px] text-slate-600">
                        Action / Charge
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCheckInOutRows.map((row, index) => (
                      <tr
                        key={`${row.isoDate}-${row.room}-${index}`}
                        className="border-b hover:bg-sky-50/30"
                      >
                        <td className="whitespace-nowrap px-2 py-2 text-[10px] font-medium text-slate-700">
                          {row.date}
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-[10px] font-medium text-slate-700">
                            {row.venue}
                          </div>
                          <div className="text-[9px] text-slate-400">
                            {row.property}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-xs font-bold text-sky-800">
                          {row.room}
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-[10px] font-medium text-slate-700">
                            {row.tour}
                          </div>
                          <div className="text-[9px] text-slate-400">
                            {row.operator}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center text-xs font-semibold text-slate-700">
                          {row.pax}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-[10px] text-slate-600">
                          {row.plannedCheckIn}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-[10px] font-semibold text-emerald-700">
                          {row.actualCheckIn}
                        </td>
                        <td className="px-2 py-2">
                          <Badge
                            variant="outline"
                            className={
                              row.earlyCheckIn.includes("early")
                                ? "border-amber-200 bg-amber-50 text-[9px] text-amber-700"
                                : "border-slate-200 bg-white text-[9px] text-slate-500"
                            }
                          >
                            {row.earlyCheckIn}
                          </Badge>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-[10px] text-slate-600">
                          {row.plannedCheckOut}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-[10px] font-semibold text-rose-600">
                          {row.actualCheckOut}
                        </td>
                        <td className="px-2 py-2">
                          <Badge
                            variant="outline"
                            className={
                              row.lateCheckOut.includes("late")
                                ? "border-rose-200 bg-rose-50 text-[9px] text-rose-700"
                                : "border-slate-200 bg-white text-[9px] text-slate-500"
                            }
                          >
                            {row.lateCheckOut}
                          </Badge>
                        </td>
                        <td className="px-2 py-2">
                          <Badge
                            className={
                              row.movementStatus === "Checked in" ||
                              row.movementStatus === "In house"
                                ? "bg-emerald-50 text-emerald-700"
                                : row.movementStatus === "Checked out"
                                  ? "bg-sky-50 text-sky-700"
                                  : "bg-amber-50 text-amber-700"
                            }
                          >
                            {row.movementStatus}
                          </Badge>
                        </td>
                        <td className="px-2 py-2 text-[10px] text-slate-600">
                          {row.action}
                        </td>
                      </tr>
                    ))}
                    {!activeCheckInOutRows.length && (
                      <tr>
                        <td
                          colSpan={13}
                          className="py-10 text-center text-xs text-slate-500"
                        >
                          No room movements match the selected date, venue and
                          property filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reports Grid */}
        {activeReport === null && (
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              {filteredReports.length} reports available
            </h2>
            <div className="grid grid-cols-4 gap-4">
              {filteredReports.map((report) => (
                <Card
                  key={report.id}
                  className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div
                        className={`w-10 h-10 rounded-lg ${report.color} flex items-center justify-center ${report.iconColor}`}
                      >
                        {report.icon}
                      </div>
                      {report.badge && (
                        <Badge
                          className={`text-xs ${
                            report.badge === "Popular"
                              ? "bg-blue-100 text-blue-700 border-blue-200"
                              : report.badge === "Live"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-purple-100 text-purple-700 border-purple-200"
                          }`}
                          variant="outline"
                        >
                          {report.badge}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      {report.title}
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">
                      {report.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                      <span>{report.category}</span>
                      <span>Updated {report.lastGenerated}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 h-7 text-xs gap-1"
                        style={{ background: "#5B9BD5" }}
                        onClick={() => handleGenerate(report.id)}
                        disabled={generatingReport === report.id}
                      >
                        {generatingReport === report.id ? (
                          <>
                            <RefreshCw className="h-3 w-3 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Eye className="h-3 w-3" />
                            View
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 p-0"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
